import commerceConfig from '@commerce/config'
import usePrice from '@commerce/product/use-price'
import type { Product } from '@commerce/types/product'
import ColourCardProcess from '@components/containers/ColourCardProcess'
import ProductByGardenPotProcess from '@components/containers/ProductByGardenPotProcess'
import ProductByLitreProcess from '@components/containers/ProductByLitreProcess'
import ProductBySamplePotProcess from '@components/containers/ProductBySamplePotProcess'
import { Button, useUI } from '@components/ui'
import WYSIWYG from '@components/ui/WYSIWYG'
import { B2B_TAG } from '@lib/helpers/general'
import useCustomer from '@lib/hooks/useCustomer'
import useScrollToTop from '@lib/hooks/useScrollToTop'
import processLocale from '@lib/locale'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProductCartButtonText from '../ProductCartButtonText'
import ExtraInfo from '../ProductSidebar/ExtraInfo'
import SampleProductOptionLabel from '../SampleProductOptionLabel'

interface TabbedProductSiblingOptionsProps {
  siblings: Product[]
  selectedSibling: Product | undefined
  setSelectedSibling: React.Dispatch<React.SetStateAction<Product | undefined>>
  currencyCode?: string
  parentProduct: Product
  cart: any
  prismicContent: any
  addSampleProductSiblingToCart: () => void
  loading: boolean
}

function TabbedProductSiblingOptions({
  siblings,
  currencyCode = 'AUD',
  selectedSibling,
  setSelectedSibling,
  parentProduct,
  cart,
  prismicContent,
  addSampleProductSiblingToCart,
  loading,
}: TabbedProductSiblingOptionsProps) {
  const { t } = useTranslation('product')
  const { t: tcommon } = useTranslation(['common'])
  const { isB2B } = useCustomer()
  const { locale } = useRouter()
  const {
    config: { context },
  } = commerceConfig
  const currentLocale = processLocale(locale)
  const currentContext = context?.[currentLocale]
  const taxAmount = currentContext?.taxAmount
  const [activeTab, setActiveTab] = useState(0)
  const [totalLitres, setTotalLitres] = useState(0)
  const {
    setSidebarView,
    openSidebar,
    selectedSurfaceTitle,
    setSelectedSurfaceTitle,
    setSurfaceArea,
    setSelectedSurfaceIndex,
  } = useUI()

  const { declarationText } = prismicContent

  const componentRef = useScrollToTop(134)

  const samples = useMemo(
    () =>
      siblings.filter(
        (sibling) =>
          sibling.productType === 'Colour Card' ||
          sibling.productType === 'Sample Pot'
      ),
    [siblings]
  )
  const gardenPotPaint = useMemo(
    () =>
      siblings.filter((sibling) => sibling.productType === 'Garden Pot Paint'),
    [siblings]
  )
  const paint = useMemo(
    () => siblings.filter((sibling) => sibling.productType === 'Paint'),
    [siblings]
  )

  const tabs = [
    { label: `${t('samples')}`, products: samples },
    { label: `${t('paint')}`, products: paint },
    { label: `${t('garden-pot-paint')}`, products: gardenPotPaint },
  ]

  const getProductBasePrice = (sibling: Product) => {
    const prices = sibling?.variants?.map((v) => v.price)
    return Math.min(...prices)
  }

  const getProductListPrice = (sibling: Product) => {
    const prices = sibling?.variants?.map((v) => v?.originalPrice || 0)
    return Math.min(...prices)
  }

  const productUnavailable = parentProduct?.tags?.includes('unavailable')

  const sampleItemPrice = selectedSibling?.variants[0].price!

  const { price: sampleItemsBeforeTaxTotal } = usePrice({
    amount: sampleItemPrice,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('excl-vat')})`,
  })

  const { price: sampleItemsAfterTaxTotal } = usePrice({
    amount: sampleItemPrice ? sampleItemPrice * (1 + taxAmount || 1) : 0,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('incl-vat-approx')})`,
  })
  const renderTabContent = () => {
    if (activeTab === 0) {
      // Render SampleProductOptionLabel for Samples tab
      return (
        <div>
          <div className="flex items-center justify-between pb-4">
            <div className="body-1-bold">{t('pick-a-type')}:</div>
            <div
              className="body-2 cursor-pointer underline"
              onClick={() => {
                setSidebarView('RIGHT_SAMPLE_SIDEBAR_VIEW')
                openSidebar()
              }}
            >
              {t('find-the-right-sample')}
            </div>
          </div>
          <div role="listbox" className="flex w-full gap-4">
            {tabs[activeTab].products.map((v, i) => {
              const onClick = () => setSelectedSibling(v)
              return (
                <SampleProductOptionLabel
                  key={i}
                  selected={selectedSibling?.id === v?.id}
                  name={v.siblingOptionLabel}
                  price={getProductBasePrice(v)}
                  listPrice={getProductListPrice(v)}
                  currencyCode={currencyCode}
                  multiplePrice={v?.variants.length > 1}
                  isB2bCustomer={isB2B}
                  isB2bProduct={v.tags?.includes(B2B_TAG)}
                  onClick={onClick}
                />
              )
            })}
          </div>
          {selectedSibling && selectedSibling?.variants.length > 1 ? (
            <>
              {selectedSibling.productType === 'Sample Pot' ? (
                <ProductBySamplePotProcess
                  options={selectedSibling.variants}
                  currencyCode={currencyCode}
                  selectedSibling={selectedSibling}
                  parentProduct={parentProduct}
                  prismicContent={prismicContent}
                  cart={cart}
                />
              ) : (
                <></>
              )}
              {selectedSibling.productType === 'Colour Card' &&
              locale == 'en-us' ? (
                <ColourCardProcess
                  options={selectedSibling.variants}
                  currencyCode={currencyCode}
                  selectedSibling={selectedSibling}
                  parentProduct={parentProduct}
                />
              ) : (
                <>
                  {selectedSibling.productType === 'Colour Card' && (
                    <>
                      <div className="my-8 h-px w-full bg-ui-grey-25" />
                      <div className="label mb-8 flex justify-between">
                        <div className="font-bold uppercase">
                          {tcommon('total')}
                        </div>
                        <div className="flex flex-col text-right">
                          {locale?.endsWith('eu')
                            ? sampleItemsAfterTaxTotal
                            : ''}
                          <span>{`${sampleItemsBeforeTaxTotal} ${
                            locale?.endsWith('gb') ? `(incl. GST / VAT)` : ''
                          }`}</span>
                        </div>
                      </div>
                      <Button
                        aria-label={`${t('add-to-cart')}`}
                        type="button"
                        width="100%"
                        onClick={addSampleProductSiblingToCart}
                        loading={loading}
                        disabled={
                          selectedSibling?.availableForSale === false ||
                          !selectedSibling ||
                          productUnavailable
                        }
                      >
                        <ProductCartButtonText
                          variant={selectedSibling?.variants[0]}
                          productUnavailable={productUnavailable}
                        />
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="my-8 h-px w-full bg-ui-grey-25" />
              {selectedSibling ? (
                <div className="label mb-8 flex justify-between">
                  <div className="font-bold uppercase">{tcommon('total')}</div>
                  <div className="flex flex-col text-right">
                    {locale?.endsWith('eu') ? sampleItemsAfterTaxTotal : ''}
                    <span>{`${sampleItemsBeforeTaxTotal} ${
                      locale?.endsWith('gb') ? `(incl. GST / VAT)` : ''
                    }`}</span>
                  </div>
                </div>
              ) : (
                <></>
              )}
              <Button
                aria-label={`${t('add-to-cart')}`}
                type="button"
                width="100%"
                onClick={addSampleProductSiblingToCart}
                loading={loading}
                disabled={
                  selectedSibling?.availableForSale === false ||
                  !selectedSibling ||
                  productUnavailable
                }
              >
                <ProductCartButtonText
                  variant={selectedSibling?.variants[0]}
                  productUnavailable={productUnavailable}
                />
              </Button>
            </>
          )}
        </div>
      )
    } else if (activeTab === 1) {
      // Render ProductByLitreProcess for Paint tab
      return (
        <ProductByLitreProcess
          options={tabs[activeTab].products[0]?.variants}
          currencyCode={currencyCode}
          selectedSurfaceTitle={selectedSurfaceTitle}
          selectedSibling={paint[0]}
          totalLitres={totalLitres}
          setTotalLitres={setTotalLitres}
          parentProduct={parentProduct}
          prismicContent={prismicContent}
          cart={cart}
        />
      )
    } else if (activeTab === 2) {
      // Render ProductByGardenPotProcess for Garden Pot Paint tab
      return (
        <ProductByGardenPotProcess
          options={tabs[activeTab].products[0]?.variants}
          currencyCode={currencyCode}
          selectedSibling={gardenPotPaint[0]}
          parentProduct={parentProduct}
          prismicContent={prismicContent}
          cart={cart}
        />
      )
    }
  }

  return (
    <div ref={componentRef}>
      <div className="relative top-[1px] flex gap-x-2">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => {
              if (activeTab !== index) {
                setActiveTab(index)
                setSelectedSurfaceTitle('')
                setSelectedSurfaceIndex(-1)
                setSurfaceArea(0)
              }
            }}
            className={cn(
              'label-bold rounded-t border-x border-t border-ui-grey-25 px-4 py-2 uppercase md:px-5 md:py-3',
              {
                'border-b border-ui-grey-25 bg-ui-light-grey':
                  activeTab !== index,
              },
              { 'border-b border-b-white': activeTab == index }
            )}
          >
            <div className={cn({ 'opacity-75': activeTab !== index })}>
              {tab.label}
            </div>
          </button>
        ))}
      </div>

      <div className="rounded-sm border border-ui-grey-25 px-4 py-8">
        {renderTabContent()}
      </div>
      {declarationText &&
      activeTab === 1 &&
      selectedSurfaceTitle !== '' &&
      totalLitres > 0 ? (
        <div className="body-2 mt-auto pt-6 pb-4 text-ui-dark-grey">
          <WYSIWYG content={declarationText} />
        </div>
      ) : null}
      <ExtraInfo />
    </div>
  )
}

export default memo(TabbedProductSiblingOptions)
